export interface DiffRowData {
  label: string;
  current: string;
  proposed: string;
  proposedMeta: string;
  isChanged?: boolean;
}

export interface GapActionFlagPillData {
  label: string;
  accessibilityLabel: string;
  dimensionId: string;
}

export interface ConceptCheckQuestionOption {
  text: string;
  isCorrect?: boolean;
}

export interface ConceptCheckQuestion {
  question: string;
  points: string;
  options: ConceptCheckQuestionOption[];
  correctFeedback?: string;
  incorrectFeedback?: string;
}

export const COURSE_OVERVIEW_DRAWER_CONTENT = {
  title: "Course Overview",
  accessibilityLabel: "Course Overview gap action",
  openEditorLabel: "Open Course Overview in editor",
  queueLabel: "Add Course Overview to queue",
  flaggedInLabel: "Flagged in",
  flaggedPills: [
    {
      label: "CLO coverage",
      accessibilityLabel: "Open CLO coverage in gap analysis",
      dimensionId: "clo-coverage",
    },
    {
      label: "OLC Quality Scorecard 3.1 - 2",
      accessibilityLabel: "Open OLC Quality Scorecard 3.1 in gap analysis",
      dimensionId: "rubric-quality",
    },
    {
      label: "Industry alignment",
      accessibilityLabel: "Open Industry alignment in gap analysis",
      dimensionId: "industry-alignment",
    },
  ] satisfies GapActionFlagPillData[],
  moduleBadgeLabel: "Module Description",
  actionTitle: "Course Overview",
  reasonTitle: "Why we're recommending changes",
  reasonText:
    "Module descriptions provide students with learning objectives, activity overviews, and instructional context at the module level. A clear, well-structured description helps students understand expectations before engaging with module content.",
  currentHeader: "Current",
  proposedHeader: "Proposed",
  editAccessibilityLabel: "Edit proposed Course Overview",
  editLabel: "Edit",
  diffRows: [
    {
      label: "Name",
      current: "Course Overview",
      proposed: "Course Overview",
      proposedMeta: "No change",
    },
    {
      label: "Description",
      current: "Welcome to Survey of Western Art. This module contains the course syllabus and instructor information.",
      proposed:
        "Welcome to Survey of Western Art. This module contains the course syllabus, instructor contact information, office hours, and links to museum partners and required readings. Please review the syllabus thoroughly before proceeding to Module 1.\n\nLearning Objectives:\n- Understand the course structure, grading policy, and museum-visit expectations\n- Locate instructor office hours and preferred communication channels\n- Access the required textbook and partnered museum collections",
      proposedMeta: "Changed",
      isChanged: true,
    },
  ] satisfies DiffRowData[],
};

export const CURATORIAL_PROJECT_DRAWER_CONTENT = {
  title: "Curatorial Project: A Virtual Modernist Gallery",
  accessibilityLabel: "Curatorial Project gap action",
  openEditorLabel: "Open Curatorial Project in editor",
  queueLabel: "Add Curatorial Project to queue",
  flaggedInLabel: "Flagged in",
  flaggedPills: [
    {
      label: "OLC Quality Scorecard 3.1",
      accessibilityLabel: "Open OLC Quality Scorecard 3.1 in gap analysis",
    },
    {
      label: "AI vulnerability · 2",
      accessibilityLabel: "Open AI vulnerability in gap analysis",
    },
  ],
  projectPillLabel: "Project",
  qualityLabel: "Fair",
  dueDateLabel: "Due Apr 25, 2026",
  pointsLabel: "200 pts",
  locationLabel: "Content > Modernism > Curatorial Project",
  reviewTabLabel: "Review",
  attachmentsTabLabel: "Attachments (1)",
  attachmentSummaryLabel: "1 attachment",
  attachmentGeneratedSummaryLabel: " · 1 AI-generated",
  downloadAllLabel: "Download All",
  attachmentFileName: "Curatorial_Project_Rubric.pdf",
  attachmentGeneratedLabel: "AI-generated",
  attachmentFileType: "PDF",
  attachmentFileSize: "210 KB",
  attachmentDescription:
    'Closes the missing-rubric gap flagged on the course\'s highest-points assessment (200 points). Includes example wall label + curatorial statement so students see what "good" looks like.',
  attachmentImpactLabel: "+ OLC Quality Scorecard 3.1",
  attachmentDownloadLabel: "Download",
  accessibilityStatusLabel: "Accessible",
  whyTitle: "Why we're recommending changes",
  whyText:
    'Promising project but currently lacks a rubric, deliverable spec, and any guidance on how to write a curatorial statement or wall label. Students without curatorial experience will struggle to know what "good" looks like.',
  olcLabel: "OLC 3.2: Assessment Design",
  olcDescription: "Assessments align with learning objectives and include clear rubrics.",
  currentHeader: "Current",
  proposedHeader: "Proposed",
  editAccessibilityLabel: "Edit proposed Curatorial Project",
  editLabel: "Edit",
  diffRows: [
    {
      label: "Title",
      current: "Curatorial Project: A Virtual Modernist Gallery",
      proposed: "Curatorial Project: A Virtual Modernist Gallery",
      proposedMeta: "No change",
    },
    {
      label: "Description",
      current: "Curate a small modernist gallery and write wall labels for each work.",
      proposed:
        "Curate a small virtual gallery of 5 works from the Modernism module. Your gallery should have a clear curatorial argument that connects the works.\n\nDeliverables:\n• A 250-word curatorial statement explaining the through-line of your selection\n• A wall label for each of the 5 works (artist, title, date, medium, dimensions, 75-word interpretive note)\n• A floor-plan sketch or annotated layout (PDF or image) showing the order in which a visitor would encounter the works\n• A 1-page reflection on what you learned about curatorial decision-making\n\nRubric:\n• Curatorial argument and through-line: 60 points\n• Quality of wall labels (accuracy, clarity, interpretive insight): 60 points\n• Layout and visitor experience: 30 points\n• Reflection: 30 points\n• Mechanics and presentation: 20 points",
      proposedMeta: "Changed",
      isChanged: true,
    },
    {
      label: "Due Date",
      current: "Apr 25, 2026",
      proposed: "Apr 25, 2026",
      proposedMeta: "No change",
    },
    {
      label: "Points Possible",
      current: "200",
      proposed: "200",
      proposedMeta: "No change",
    },
  ] satisfies DiffRowData[],
};

export const MODULE_CONCEPT_CHECK_DRAWER_CONTENT = {
  title: "Module 5 Concept Check",
  accessibilityLabel: "Module 5 Concept Check gap action",
  openEditorLabel: "Open Module 5 Concept Check in editor",
  queueLabel: "Add Module 5 Concept Check to queue",
  proposedForLabel: "Proposed for",
  proposedForPillLabel: "OLC Quality Scorecard 3.1",
  proposedForAccessibilityLabel: "Open OLC Quality Scorecard 3.1 in gap analysis",
  newPillLabel: "New",
  assessmentPillLabel: "Assessment",
  recommendationMetaLabel: "Recommended new activity",
  heading: "Module 5 Concept Check",
  whyTitle: "Why we're suggesting this",
  whyText:
    "Module 5 currently has no end-of-module assessment. The final essay is open-ended and does not check whether students engaged with the module's reading.",
  learningObjectiveText:
    "Learning objective: Demonstrate familiarity with at least three contemporary artists working outside the Western canon",
  olcLabel: "OLC 3.3: Feedback",
  olcDescription: "Students receive timely feedback on assessments.",
  descriptionTitle: "Description",
  descriptionText:
    "Ten-question multiple-choice quiz covering the Module 5 reading on contemporary practice - identity, diaspora, and global curatorial frameworks. Includes three image-identification items drawn from the artists named in the reading.",
  currentQuestionsLabel: "Current questions",
  currentQuestionsCount: "0",
  proposedChangesLabel: "Proposed changes",
  proposedChangesCount: "10 added",
  newQuestionLabel: "New question",
  questions: [
    {
      question: 'The phrase "decentering the canon" most directly refers to which contemporary curatorial move?',
      points: "4 points",
      options: [
        { text: "Eliminating Western art from museum collections." },
        {
          text: "Restructuring narratives so the Western lineage is one history among many, not the default lens.",
          isCorrect: true,
        },
        { text: "Replacing chronological hangs with thematic ones." },
        { text: "Acquiring more contemporary artists than historical ones." },
      ],
      correctFeedback:
        "Right. Decentering is about the frame, not the inventory - the canon stops being the default lens through which everything else is read.",
      incorrectFeedback:
        "Decentering doesn't mean removal or replacement; it means the Western canon stops being the default narrative against which other histories are positioned.",
    },
    {
      question: "In a diasporic visual practice, the artist most often:",
      points: "4 points",
      options: [
        { text: "Works exclusively in their country of origin." },
        {
          text: "Negotiates between heritage and present location, often using materials or symbols that carry that doubled reference.",
          isCorrect: true,
        },
        { text: "Limits subject matter to the experience of immigration." },
        { text: "Avoids referencing personal background to keep the work universal." },
      ],
    },
    {
      question:
        "Image identification: an artist drapes large-scale shimmering tapestries made from thousands of folded and stitched aluminum bottle caps. Whose work is this?",
      points: "4 points",
      options: [
        { text: "Yinka Shonibare" },
        { text: "El Anatsui", isCorrect: true },
        { text: "Theaster Gates" },
        { text: "Wangechi Mutu" },
      ],
      correctFeedback:
        "Right - El Anatsui (Ghanaian, b. 1944). The bottle-cap tapestries reference both West African textile traditions and the global liquor trade's colonial residue.",
      incorrectFeedback:
        "Re-read the section on El Anatsui. The bottle-cap medium is his signature - large-scale tapestries assembled from caps from West African distilleries.",
    },
    {
      question:
        "Image identification: a sculpture of a headless mannequin in 18th-century European dress, but the fabric is brightly patterned Dutch wax cotton. Whose work is this?",
      points: "4 points",
      options: [
        { text: "Yinka Shonibare", isCorrect: true },
        { text: "Kara Walker" },
        { text: "Glenn Ligon" },
        { text: "Kerry James Marshall" },
      ],
      correctFeedback:
        "Right. Shonibare's use of Dutch wax fabric collapses the assumption that African textile and European costume are stable, separable categories.",
      incorrectFeedback:
        "Look up Yinka Shonibare. The Dutch wax cotton is the giveaway - its hybrid origin is exactly the point of the sculpture.",
    },
    {
      question:
        "Image identification: a black-paper cut-paper silhouette panorama depicting antebellum American figures and scenes of violence, installed directly on a gallery wall. Whose work is this?",
      points: "4 points",
      options: [
        { text: "Wangechi Mutu" },
        { text: "Mona Hatoum" },
        { text: "Kara Walker", isCorrect: true },
        { text: "Julie Mehretu" },
      ],
      correctFeedback:
        "Right. Walker's cut-paper silhouettes weaponize a 19th-century parlor craft - the form reads as decorative until the content registers.",
      incorrectFeedback:
        "Re-read the Walker section. The cut-paper silhouette technique paired with antebellum subject matter is her signature.",
    },
    {
      question:
        "The proliferation of international biennials since the 1990s has had which effect on contemporary art?",
      points: "4 points",
      options: [
        { text: "Eliminated the centrality of New York and London galleries." },
        {
          text: "Created a parallel global circuit where artists outside the Western market gain visibility, while raising critiques about access and curatorial labor.",
          isCorrect: true,
        },
        { text: "Standardized contemporary art into a single recognizable style." },
        { text: "Replaced museum exhibitions as the primary venue for new work." },
      ],
      incorrectFeedback:
        "The Western market wasn't replaced; a parallel circuit emerged. The reading flags both the access it created and the new uneven labor it produced.",
    },
    {
      question:
        "Theaster Gates's practice - combining social-housing redevelopment in Chicago with gallery exhibition of objects salvaged from those buildings - is best described as:",
      points: "4 points",
      options: [
        { text: "Outsider art with no institutional engagement." },
        {
          text: "Social practice - work whose subject and method are inseparable from a community context.",
          isCorrect: true,
        },
        { text: "Documentary photography of urban decline." },
        { text: "Performance art staged in non-traditional venues." },
      ],
    },
    {
      question: "Decolonizing the museum as a curatorial commitment most often involves which combination of moves?",
      points: "4 points",
      options: [
        { text: "Removing all colonial-era acquisitions from display." },
        {
          text: "Reattributing wall labels, restituting objects acquired under colonial conditions, and re-narrating sections with input from communities of origin.",
          isCorrect: true,
        },
        { text: "Hiring more curators of color while leaving display practices unchanged." },
        { text: 'Creating a separate "decolonial" gallery for non-Western objects.' },
      ],
      incorrectFeedback:
        "Decolonization is a labels-restitution-narrative project, not a removal project. Performative gestures miss the structural work.",
    },
    {
      question: 'In contemporary practice, the term "transnational" describes work that:',
      points: "4 points",
      options: [
        { text: "Was made in one country and exhibited in another." },
        {
          text: "Operates across national frames simultaneously, refusing any single national identity as its primary frame of reference.",
          isCorrect: true,
        },
        { text: "Was made by an artist who has emigrated." },
        { text: "Addresses immigration policy as its subject." },
      ],
    },
    {
      question:
        "Which is the strongest reason a contemporary survey course must address artists working outside the Western canon, beyond representational fairness alone?",
      points: "4 points",
      options: [
        { text: "Diversification expectations in higher education." },
        {
          text: "The major formal and conceptual moves in late-20th- and 21st-century art cannot be told accurately without these artists.",
          isCorrect: true,
        },
        { text: "Western artists no longer produce significant new work." },
        { text: "Students prefer non-Western art." },
      ],
      correctFeedback:
        "Right. The argument is methodological, not symbolic - leaving these artists out produces an inaccurate contemporary survey.",
      incorrectFeedback:
        "Re-read why representation in the survey is a methodological requirement, not a symbolic concession. The story of contemporary art doesn't cohere without these artists.",
    },
  ] satisfies ConceptCheckQuestion[],
};

export const DRAWER_FOOTER_CONTENT = {
  addToQueueLabel: "Add to Queue",
  skipLabel: "Skip",
  openInEditorLabel: "Open in editor",
  skipAccessibilityLabel: "Skip recommendation",
};
